// GR CONNECT module registry + effective-permission resolution.
//
// Source of truth for access is ALWAYS the attendance backend (it 403s unauthorized calls).
// This file only drives which sidebar entries/routes the browser UI shows:
//   - superadmin_bypass === true (from GET /api/permissions/me) → full access
//   - role "SuperAdmin" → full access (belt & braces; production SuperAdmin account)
//   - role "Admin" → legacy admin module set (preserves pre-GR-CONNECT behavior)
//   - explicit permission grants from /permissions/me are unioned on top, matched
//     defensively by module-name aliases because the exact grant vocabulary is backend-defined.
// Ordinary employees (no admin role, no grants) see only personal modules.
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard, Users, Layers, BadgeCheck, Award, Clock3, CalendarCheck, CalendarDays,
  UserPlus, FileBadge, ClipboardList, FileSpreadsheet, ShieldCheck, KeyRound, Database,
  ScrollText, Settings, UserCircle, HardHat, ShoppingCart, Truck, Warehouse, Laptop,
  LifeBuoy, Stamp, FolderOpen, LockKeyhole, Radar, MapPin,
} from "lucide-react";
import { staffApi, getUser, getCachedPerms, setCachedPerms } from "./api";

// ---------- module registry (drives sidebar + route guards) ----------
// personal: visible to every signed-in employee.
// pending: roadmap module — renders a "Setup Pending" page; visible to privileged users only.
export const MODULES = [
  { key: "dashboard", label: "Dashboard", to: "/app", end: true, icon: LayoutDashboard, personal: true },

  { key: "hrms.employees", label: "Employee Master", to: "/app/hrms/employees", icon: Users, section: "HRMS", aliases: ["employee", "users", "hrms"] },
  { key: "hrms.departments", label: "Departments", to: "/app/hrms/departments", icon: Layers, section: "HRMS", aliases: ["department"] },
  { key: "hrms.designations", label: "Designations", to: "/app/hrms/designations", icon: BadgeCheck, section: "HRMS", aliases: ["designation"] },
  { key: "hrms.grades", label: "Grades", to: "/app/hrms/grades", icon: Award, section: "HRMS", aliases: ["grade"] },
  { key: "hrms.shifts", label: "Shifts", to: "/app/hrms/shifts", icon: Clock3, section: "HRMS", aliases: ["shift"] },
  { key: "hrms.leaves", label: "Leave Management", to: "/app/hrms/leaves", icon: CalendarCheck, section: "HRMS", aliases: ["leave"] },
  { key: "hrms.holidays", label: "Holidays", to: "/app/hrms/holidays", icon: CalendarDays, section: "HRMS", aliases: ["holiday"] },
  { key: "hrms.recruitment", label: "Recruitment", to: "/app/hrms/recruitment", icon: UserPlus, section: "HRMS", pending: true, aliases: ["recruit"] },
  { key: "hrms.documents", label: "Employee Documents", to: "/app/hrms/documents", icon: FileBadge, section: "HRMS", pending: true, aliases: ["employee_document", "empdoc"] },

  { key: "attendance.mine", label: "My Attendance", to: "/app/attendance", icon: ClipboardList, section: "Attendance", personal: true },
  { key: "attendance.live", label: "Live Staff Monitor", to: "/app/attendance/live", icon: Radar, section: "Attendance", aliases: ["attendance", "monitor", "live", "presence"] },
  { key: "attendance.all", label: "All Employee Attendance", to: "/app/attendance/all", icon: ClipboardList, section: "Attendance", aliases: ["attendance"] },
  { key: "attendance.reports", label: "Attendance Reports", to: "/app/reports", icon: FileSpreadsheet, section: "Attendance", aliases: ["report"] },

  { key: "erp.projects", label: "Projects & Sites", to: "/app/modules/projects", icon: HardHat, pending: true, aliases: ["project"] },
  { key: "erp.purchase", label: "Purchase", to: "/app/modules/purchase", icon: ShoppingCart, pending: true, aliases: ["purchase", "procurement"] },
  { key: "erp.vendors", label: "Vendors", to: "/app/modules/vendors", icon: Truck, pending: true, aliases: ["vendor", "supplier"] },
  { key: "erp.stores", label: "Stores & Inventory", to: "/app/modules/stores", icon: Warehouse, pending: true, aliases: ["store", "inventory"] },
  { key: "erp.assets", label: "Assets", to: "/app/modules/assets", icon: Laptop, pending: true, aliases: ["asset"] },
  { key: "erp.helpdesk", label: "IT Helpdesk", to: "/app/modules/helpdesk", icon: LifeBuoy, pending: true, aliases: ["helpdesk", "ticket"] },
  { key: "erp.approvals", label: "Approvals", to: "/app/modules/approvals", icon: Stamp, pending: true, aliases: ["approval"] },
  { key: "erp.documents", label: "Documents", to: "/app/modules/documents", icon: FolderOpen, pending: true, aliases: ["document"] },

  { key: "reports", label: "Reports", to: "/app/reports", icon: FileSpreadsheet, aliases: ["report"] },

  { key: "admin.users", label: "Users", to: "/app/admin/users", icon: Users, section: "Admin", aliases: ["user"] },
  { key: "admin.login_management", label: "Login Management", to: "/app/admin/login-management", icon: LockKeyhole, section: "Admin", superOnly: true, aliases: ["login_management", "login"] },
  { key: "admin.roles", label: "Roles", to: "/app/admin/roles", icon: ShieldCheck, section: "Admin", superOnly: true, aliases: ["role"] },
  { key: "admin.permissions", label: "Permissions", to: "/app/admin/permissions", icon: KeyRound, section: "Admin", superOnly: true, aliases: ["permission"] },
  { key: "admin.master_data", label: "Master Data", to: "/app/admin/master-data", icon: Database, section: "Admin", aliases: ["master"] },
  { key: "admin.properties", label: "Properties & Geofencing", to: "/app/admin/properties", icon: MapPin, section: "Admin", superOnly: true, aliases: ["branch", "geofence", "property", "site"] },
  { key: "admin.audit_logs", label: "Audit Logs", to: "/app/admin/audit-logs", icon: ScrollText, section: "Admin", superOnly: true, aliases: ["audit", "log"] },
  { key: "admin.settings", label: "Settings", to: "/app/admin/settings", icon: Settings, section: "Admin", superOnly: true, pending: true, aliases: ["setting"] },

  { key: "profile", label: "Profile", to: "/app/profile", icon: UserCircle, personal: true },
];

// Personal pages every employee keeps (current portal behavior): dashboard, my attendance,
// my leave, company holidays, profile.
export const PERSONAL_EXTRA = [
  { key: "leave.mine", label: "My Leave", to: "/app/leave", icon: CalendarCheck, personal: true },
  { key: "holidays.view", label: "Holidays", to: "/app/holidays", icon: CalendarDays, personal: true },
];

// Modules the legacy "Admin" role could reach before GR CONNECT — preserved as the fallback set.
const LEGACY_ADMIN_SET = new Set([
  "hrms.employees", "hrms.departments", "hrms.designations", "hrms.grades", "hrms.shifts",
  "hrms.leaves", "hrms.holidays", "attendance.all", "attendance.reports", "reports",
  "admin.users", "admin.master_data",
]);

// ---------- permission parsing (defensive — exact backend vocabulary unknown) ----------
const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

const permEntries = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  for (const k of ["permissions", "grants", "modules", "effective_permissions", "items"]) {
    if (Array.isArray(raw[k])) return raw[k];
  }
  return [];
};

const entryModules = (e) => {
  if (typeof e === "string") return [e];
  if (!e || typeof e !== "object") return [];
  if (Array.isArray(e.modules)) return e.modules;
  return [e.module, e.name, e.key, e.code].filter(Boolean);
};

// ---------- access resolution ----------
// /api/permissions/me is the AUTHORITATIVE permission source. When it answers, the
// SuperAdmin bypass comes EXCLUSIVELY from superadmin_bypass === true — a cached/stale
// role string is never trusted on its own.
//
// TEMPORARY COMPATIBILITY FALLBACK (documented, to be removed once /permissions/me is
// confirmed live for all accounts): only when the permissions endpoint is UNAVAILABLE
// (network error / 404 / non-JSON), the session role string is used — "SuperAdmin" → full
// access, "Admin" → the pre-GR-CONNECT admin module set. When the endpoint IS available,
// normal users derive access purely from their effective permission entries.
export const resolveAccess = (user, perms, permsAvailable) => {
  // Bypass flag read from the authoritative endpoint payload. Primary key per spec is
  // superadmin_bypass; two documented key aliases add robustness against endpoint-side
  // key naming drift — all still come from the endpoint, never from the client role string.
  const bypassFlag = permsAvailable
    ? [perms?.superadmin_bypass, perms?.is_superadmin, perms?.superadmin].some((v) => v === true)
    : false;
  const superadmin = permsAvailable
    ? bypassFlag
    : user?.role === "SuperAdmin"; // compat fallback only
  const legacyAdmin = permsAvailable
    ? false // role alone never grants when the endpoint answered — grants only
    : user?.role === "Admin"; // compat fallback only

  const granted = new Set();
  for (const e of permEntries(perms)) {
    for (const m of entryModules(e)) {
      const nm = norm(m);
      if (!nm) continue;
      for (const mod of MODULES) {
        if ((mod.aliases || []).some((a) => nm.includes(norm(a)) || norm(a).includes(nm))) granted.add(mod.key);
      }
    }
  }

  const can = (key) => {
    const mod = MODULES.find((m) => m.key === key) || PERSONAL_EXTRA.find((m) => m.key === key);
    if (!mod) return false;
    if (mod.personal) return true;
    if (superadmin) return true;
    if (granted.has(key)) return true;
    if (legacyAdmin) {
      if (mod.superOnly) return false; // roles/permissions/audit/settings stay SuperAdmin-only
      if (mod.pending) return true;    // placeholders visible to admins, hidden from employees
      return LEGACY_ADMIN_SET.has(key);
    }
    return false;
  };

  const adminArea = superadmin || legacyAdmin || [...granted].some((k) =>
    k.startsWith("hrms.") || k.startsWith("admin.") || k === "attendance.all" || k === "reports");

  return { superadmin, legacyAdmin, granted, can, adminArea };
};

// ---------- react hook ----------
let inflight = null;

export const useAccess = () => {
  const user = getUser();
  const [state, setState] = useState(() => {
    const cached = getCachedPerms();
    // A cached payload means the endpoint answered successfully earlier this session.
    return { perms: cached, available: cached !== null };
  });

  useEffect(() => {
    if (state.available || !user) return;
    if (!inflight) {
      inflight = staffApi.get("/permissions/me")
        .then((r) => { const data = r.data ?? {}; setCachedPerms(data); return { ok: true, data }; })
        .catch(() => ({ ok: false, data: null })) // endpoint unavailable → documented role fallback applies
        .finally(() => { inflight = null; });
    }
    inflight.then((res) => setState({ perms: res.data, available: res.ok }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(
    () => ({ user, perms: state.perms, permsAvailable: state.available, ...resolveAccess(user, state.perms, state.available) }),
    [user, state]
  );
};

export const moduleForPath = (pathname) => {
  const all = [...MODULES, ...PERSONAL_EXTRA];
  let best = null;
  for (const m of all) {
    if (m.end ? pathname === m.to : (pathname === m.to || pathname.startsWith(m.to + "/"))) {
      if (!best || m.to.length > best.to.length) best = m;
    }
  }
  if (pathname.startsWith("/app/admin")) return MODULES.find((m) => m.key === "admin.users") && (best || { key: "admin.area", label: "Admin" });
  return best;
};
